import React, { useEffect, useState, useRef } from "react"; 
import axios from "axios";

import io from "socket.io-client";
import { ChatRoomViewNode } from "../../components/ForumMode/ForumChatRoom/ChatRoomViewNode";
import Notice from "../../components/Notice";
import url from "../../url.js";
import { genEdge, genNode, newNode, newEdge } from "../../utils/ideaTool";
import { useLocation } from "react-router-dom";
import { Button, TextField, IconButton } from "@mui/material";
import CallAgent from '../../components/CallAgent';
import DOMPurify from 'dompurify';
import ReplyIcon from '@mui/icons-material/Reply';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ChatRoomCreateReply } from "../../components/ForumMode/ForumChatRoom/ChatRoomCreateReply";
import { buildChatRoomContext } from "../../components/ForumMode/ForumChatRoom/chatRoomContext";
import { createNodeWithPartnerResponse } from "../../utils/nodeCreationHelper";
import useUnreadMessages from "../../hooks/useUnreadMessages";
import GroupToolPanel from "../../components/GroupToolPanel";
import { getNodeType } from "../../utils/nodeTypeUtils";
import { resolveViewNodeComponent } from "../../components/ForumMode/nodeViewRegistry";
import semanticColors from '../../styles/semanticColors';

/**
 * QuestionnaireMessageBubble - renders questionnaire JSON content in the chat bubble.
 * Shows description and a numbered question list instead of raw JSON.
 */
function QuestionnaireMessageBubble({ content }) {
    let data = null;
    try {
        data = JSON.parse(content);
    } catch (_) {}

    if (!data || !Array.isArray(data.questions)) {
        return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />;
    }

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <span style={{
                    backgroundColor: '#22c55e',
                    color: 'white',
                    borderRadius: '4px',
                    padding: '2px 8px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                }}>
                    📋 問卷
                </span>
            </div>
            {data.description && (
                <div style={{ fontSize: '13px', color: '#475569', marginBottom: '8px' }}>
                    {data.description}
                </div>
            )}
            <ol style={{ margin: 0, paddingLeft: '20px' }}>
                {data.questions.map((q, idx) => (
                    <li key={q.id ?? idx} style={{ fontSize: '13px', marginBottom: '4px' }}>
                        {q.text}
                    </li>
                ))}
            </ol>
        </div>
    );
}

export default function ForumModes_chatroom({ showNavbar = true }) {
    // ✅ 初始化：將 localStorage 同步到 sessionStorage
    useEffect(() => {
        const syncStorage = () => {
            const activityId = localStorage.getItem('activityId');
            const groupId = localStorage.getItem('groupId');
            const userId = localStorage.getItem('userId');
            const joinCode = localStorage.getItem('joinCode');
            
            if (activityId) sessionStorage.setItem('activityId', activityId);
            if (groupId) sessionStorage.setItem('groupId', groupId);
            if (userId) sessionStorage.setItem('userId', userId);
            if (joinCode) sessionStorage.setItem('joinCode', joinCode);
            
            console.log('[ForumChatRoom] Storage 同步完成:', { activityId, groupId, userId, joinCode });
        };
        
        syncStorage();
    }, []);
    
    const activityInfo = JSON.parse(localStorage.getItem('activityInfo'));
    const { updateLastReadTime, clearCache } = useUnreadMessages();
    
    // 訪客模式檢測（精簡，從 sessionStorage 讀取）
    const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';
    
    // Single source of truth for nodes and edges
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    
    const [open, setOpen] = useState(false);
    const [nodeContent, setNodeContent] = useState(null);
    const [ws, setSocket] = useState(null);
    const ownedSocketRef = useRef(null);
    const activityId = sessionStorage.getItem("activityId");
    const location = useLocation();
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [newMessageCount, setNewMessageCount] = useState(0);
    const containerRef = useRef(null);
    const [showReply, setShowReply] = useState(false);
    const [notices, setNotices] = useState([]); // Add notices state

    // Add notice functions
    const addNotice = (message, type = 'info') => {
        const id = Date.now();
        setNotices(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setNotices(prev => prev.filter(notice => notice.id !== id));
        }, 5000);
    };

    const removeNotice = (id) => {
        setNotices(prev => prev.filter(notice => notice.id !== id));
    };

    // 新增 groupId 作為狀態變量
    const [groupId, setGroupId] = useState(sessionStorage.getItem('groupId'));

    const handleClickOpen = (nodeId) => {
        setNodeContent(null);
        setOpen(true);

        // Find node in graph
        const node = nodes.find(n => n.id == nodeId);
        
        // 初始節點（包含問卷初始節點）帶有 isInitial 標記，直接用本地數據，不呼叫後端 API
        const isInitialNode = node?.isInitial === true;
        if (isInitialNode) {
            // For initial nodes, use local data
            setNodeContent({ 
                ...node,
                content: node.content,
                title: node.title
            });
        } else {
            // For other nodes, fetch from backend
            fetchNodeData(nodeId);
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const fetchNodeData = async (nodeId) => {
        const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';
        const activityId = sessionStorage.getItem('activityId');
        const groupId = sessionStorage.getItem('groupId');
        
        try {
            let response;
            if (isGuestMode) {
                // 访客模式：使用公开 API
                response = await axios.get(`${url.backendHost}api/guest/${activityId}/${groupId}/node/${nodeId}`);
            } else {
                // 认证模式：使用认证 API
                response = await axios.get(`${url.backendHost}api/nodes/${nodeId}`);
            }
            setNodeContent(response.data);
            // 
        } catch (err) {
            console.error(err);
        }
    };

    // 獲取 groupId，並在其後渲染 getNodes()
    const fetchGroupData = async () => {
        const jwtToken = localStorage.getItem('jwtToken');
        const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : undefined;
        const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';
        
        // ✅ 安全獲取參數
        const joinCode = sessionStorage.getItem("joinCode");
        const activityId = sessionStorage.getItem("activityId");
        const userId = sessionStorage.getItem("userId");
        
        // ✅ 參數驗證（訪客模式跳過 userId 檢查）
        if (!isGuestMode && (!activityId || activityId === 'null' || !userId || userId === 'null')) {
            console.error('[ForumChatRoom] 缺少必要參數:', { activityId, userId });
            alert('無法獲取活動資訊，請重新進入活動');
            window.location.href = '/main';
            return;
        }
        
        // 訪客模式不需要獲取群組資料，直接返回
        if (isGuestMode) {
            console.log('[ForumChatRoom] 訪客模式，跳過獲取群組資料');
            await getNodes();
            return;
        }
        
        const enterDifferentGroupEndpoint = `${url.backendHost}api/groups/teacher/${joinCode}/${userId}`;
        const getMyGroupEndpoint = `${url.backendHost}api/groups/${activityId}/${userId}`;
        
        console.log('[ForumChatRoom] 獲取群組資料:', { activityId, userId, joinCode });

        try {
            if (!joinCode) throw new Error('no-join-code');
            const response = await axios.get(enterDifferentGroupEndpoint, { headers, withCredentials: true });
            const fetchedGroupId = response.data.data[0].id;
            localStorage.setItem("groupId", fetchedGroupId);
            sessionStorage.setItem("groupId", fetchedGroupId);
            setGroupId(fetchedGroupId);
        } catch (error) {
            try {
                const response = await axios.get(getMyGroupEndpoint, { headers, withCredentials: true });
                const fetchedGroupId = response.data.data[0].id;
                localStorage.setItem("groupId", fetchedGroupId);
                sessionStorage.setItem("groupId", fetchedGroupId);
                setGroupId(fetchedGroupId);
            } catch (error) {
                console.error("獲取群組資料時出錯", error);
            }
        } finally {
            await getNodes();
        }
    };

    useEffect(() => {
        let isUnmounted = false;
        let socketInstance = null;
        async function fetchData() {
            await fetchGroupData();
            if (isUnmounted) return;
            socketInstance = io.connect(url.socketioHost,{path: '/s/socket.io'});
            ownedSocketRef.current = socketInstance;
            setSocket(socketInstance);
        }
        fetchData();
        return () => {
            isUnmounted = true;
            if (socketInstance) {
                socketInstance.disconnect();
                if (ownedSocketRef.current === socketInstance) {
                    ownedSocketRef.current = null;
                }
            }
        };
    }, []);

    // 標記已讀：組件掛載時更新 lastRead，並定期更新（每 2 分鐘）
    useEffect(() => {
        const gid = sessionStorage.getItem('groupId');
        const aid = sessionStorage.getItem('activityId');
        if (!gid || !aid || !updateLastReadTime) return;

        const groupIdNum = parseInt(gid, 10);
        const activityIdNum = parseInt(aid, 10);

        // 立即更新一次
        updateLastReadTime(groupIdNum, activityIdNum).catch(err => 
            console.error('ForumChatRoom 標記已讀失敗:', err)
        );

        // 每 2 分鐘定期更新
        const interval = setInterval(() => {
            updateLastReadTime(groupIdNum, activityIdNum).catch(err => 
                console.error('ForumChatRoom 定期更新已讀失敗:', err)
            );
        }, 120000); // 2 分鐘

        return () => clearInterval(interval);
    }, [groupId, updateLastReadTime]);

    // 頁面可見性變化時更新（切換回標籤頁時）
    useEffect(() => {
        const gid = sessionStorage.getItem('groupId');
        const aid = sessionStorage.getItem('activityId');
        if (!gid || !aid || !updateLastReadTime) return;

        const groupIdNum = parseInt(gid, 10);
        const activityIdNum = parseInt(aid, 10);

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                updateLastReadTime(groupIdNum, activityIdNum).catch(err => 
                    console.error('ForumChatRoom 可見性變化更新已讀失敗:', err)
                );
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [groupId, updateLastReadTime]);

    useEffect(() => {
        if (!ws || !activityId) return;

        const nodeEvent = `node-recieve-${activityId}`;
        const edgeEvent = `edge-recieve-${activityId}`;
        const handleConnect = () => {};

        const handleNodeReceive = (body) => {
            if (body.groupId == sessionStorage.getItem('groupId')) {
                const newNode = {
                    createdAt: body.createdAt || new Date().toISOString(),
                    ...genNode(body),
                };
                
                // ✅ 在 setState 回調中檢查去重，避免閉包問題
                setNodes(prevNodes => {
                    const nodeExists = prevNodes.some(node => String(node.id) === String(newNode.id));
                    if (nodeExists) {
                        console.log('[ForumChatRoom] 節點已存在，跳過:', newNode.id);
                        return prevNodes;
                    }
                    
                    const container = containerRef.current;
                    
                    // Check if user is not at bottom of chat
                    if (container) {
                        const scrollPosition = container.scrollHeight - container.scrollTop;
                        const isNearBottom = scrollPosition <= container.clientHeight + 100;
                        if (!isNearBottom) {
                            setNewMessageCount(prev => prev + 1);
                        }
                    }
                    
                    // 收到新消息，清除該群組的未讀快取（其他群組會顯示紅點）
                    if (clearCache) {
                        clearCache(body.groupId);
                    }
                    
                    return [...prevNodes, newNode];
                });
            }
        };

        const handleEdgeReceive = (body) => {
            if (body.groupId == sessionStorage.getItem("groupId")) {
                setEdges((prevEdges) => [...prevEdges, genEdge(body)]);
            }
        };

        ws.on("connect", handleConnect);
        ws.on(nodeEvent, handleNodeReceive);
        ws.on(edgeEvent, handleEdgeReceive);

        return () => {
            ws.off("connect", handleConnect);
            ws.off(nodeEvent, handleNodeReceive);
            ws.off(edgeEvent, handleEdgeReceive);
        };
    }, [ws, activityId, clearCache]);

    const getNodes = async () => {
        // ✅ 安全獲取 groupId
        const groupId = sessionStorage.getItem("groupId");
        const isGuestMode = sessionStorage.getItem("isGuestMode") === 'true';
        const activityId = sessionStorage.getItem("activityId");
        
        if (!groupId || groupId === 'null') {
            console.log('[ForumChatRoom] groupId 無效，嘗試獲取群組資料');
            if (!isGuestMode) {
                await fetchGroupData();
            }
            return;
        }

        try {
            const jwtToken = localStorage.getItem('jwtToken');
            
            // 訪客模式使用公開 API，否則使用認證 API
            let fetchData, fetchEdge;
            if (isGuestMode) {
                // 訪客模式：不發送 JWT token，使用公開 API
                fetchData = await axios.get(`${url.backendHost}api/guest/${activityId}/${groupId}/nodes`);
                fetchEdge = await axios.get(`${url.backendHost}api/guest/${activityId}/${groupId}/edges`);
            } else {
                // 認證模式：發送 JWT token
                const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : undefined;
                fetchData = await axios.get(`${url.backendHost}api/nodes/all/${groupId}`, { headers, withCredentials: true });
                fetchEdge = await axios.get(`${url.backendHost}api/edges/all/${groupId}`, { headers, withCredentials: true });
            }

            

            // 處理節點數據：訪客模式和認證模式的數據結構不同
            let nodeData;
            if (isGuestMode) {
                // 訪客模式：直接返回節點數組
                nodeData = fetchData.data.map((node) => {
                    const createdAt = node.createdAt ? new Date(node.createdAt) : null;
                    const formattedCreatedAt = createdAt ? createdAt.toISOString() : 'Invalid Date';

                    const generatedNode = {
                        ...genNode(node),
                        createdAt: formattedCreatedAt,
                    };

                    return generatedNode;
                });
            } else {
                // 認證模式：返回 { Nodes: [...] } 結構
                nodeData = fetchData.data[0].Nodes.map((node) => {
                    const createdAt = node.createdAt ? new Date(node.createdAt) : null;
                    const formattedCreatedAt = createdAt ? createdAt.toISOString() : 'Invalid Date';

                    const generatedNode = {
                        ...genNode(node),
                        createdAt: formattedCreatedAt,
                    };

                    return generatedNode;
                });
            }

            const edgeData = fetchEdge.data.map((edge) => genEdge(edge));

            // Add initial nodes if they exist
            let allNodes = [...nodeData];
            try {
                const { loadInitialNodesForChatRoom, validateInitialNodes } = require('../../utils/initialNodesLoader');
                const groupId = sessionStorage.getItem('groupId');
                const initialNodes = loadInitialNodesForChatRoom(activityInfo, groupId);
                
                if (initialNodes.length > 0) {
                    // 验证节点有效性
                    if (!validateInitialNodes(initialNodes)) {
                        console.error('[ForumChatRoom] Invalid initial nodes detected');
                    }
                    allNodes = [...initialNodes, ...allNodes];
                }
            } catch (e) {
                console.error('[ForumChatRoom] Error loading initial nodes:', e);
            }

            setNodes(allNodes);
            setEdges(edgeData);
        } catch (error) {
            console.error('獲取節點或邊時出錯:', error);
            alert("登入失效: 請重新登入");
            window.location.href = "/";
        }
    };

    const events = {
        click: (event) => {
            
            
            if (event.nodes.length === 1) {
                handleClickOpen(event.nodes[0]);
                sessionStorage.setItem('nodeId', event.nodes[0]);
            }
        },
        click_chatroom: (nodeid) => {
            
            handleClickOpen(nodeid);
            sessionStorage.setItem('nodeId', nodeid);
        },
    };

    // 聊天室介面
    const Chat = React.memo(({ data, containerRef }) => {
        const nodesMap = React.useMemo(() => data.nodes.reduce((acc, node) => {
            node.source = {...node};
            node.content = node.source.content || node.source.title;
            [node.icon, node.title, node.infoString] = node.label.split('\n\n');
            [node.author, node.datetime] = node.infoString.split('\n');
            acc[node.id] = node;
            return acc;
        }, {}), [data.nodes]);

        const uniqueEdges = React.useMemo(() => data.edges.filter((edge, index, self) =>
            index === self.findIndex((e) => e.from === edge.from && e.to === edge.to)
        ), [data.edges]);

        // ✅ 在訪客模式下，將第一個發言人當作主要發言人
        const currentUser = React.useMemo(() => {
            if (isGuestMode && data.nodes.length > 0) {
                // 找出第一個非引導單元的作者
                const firstNode = data.nodes.find(node => node.tags !== 'initial');
                if (firstNode) {
                    const [, , infoString] = firstNode.label.split('\n\n');
                    const [author] = infoString?.split('\n') || [''];
                    return author;
                }
            }
            return localStorage.getItem('userName') || '匿名用戶';
        }, [isGuestMode, data.nodes]);
        
        const messagesEndRef = useRef(null);

        const scrollToBottom = () => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            setNewMessageCount(0);
        };

        const handleScroll = (e) => {
            const container = e.target;
            
            localStorage.setItem('chatScrollPosition', container.scrollTop);
            const scrollPosition = container.scrollHeight - container.scrollTop;
            const isNearBottom = scrollPosition <= container.clientHeight + 100;
            
            if (isNearBottom) {
                setNewMessageCount(0);
            }
        };

        useEffect(() => {
            const container = containerRef.current;
            if (!container) return;

            // Restore scroll position
            const savedScrollPosition = localStorage.getItem('chatScrollPosition');
            if (savedScrollPosition) {
                
                container.scrollTop = parseInt(savedScrollPosition);
            } else {
                
                scrollToBottom();
            }
        }, [data.nodes.length]);

        return (
            <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                height: '100%',
                backgroundColor: 'transparent',
                paddingTop: '20px',
                position: 'relative',
                overflow: 'hidden',
                paddingBottom: '80px'
            }}>
                {/* Messages Container */}
                <div 
                    ref={containerRef}
                    onScroll={handleScroll}
                    style={{ 
                        flex: 1,
                        overflowY: 'auto',
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        height: '100%',
                        position: 'relative',
                        marginBottom: '20px'
                    }}
                >
                    {activityInfo && activityInfo.Activity && activityInfo.Activity.content && (
                        <div style={{
                            margin: '0 auto',
                            marginBottom: '20px',
                            borderRadius: '8px',
                            backgroundColor: 'var(--white)',
                            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                            padding: '20px',
                            width: '100%'
                        }}>
                            <p style={{ marginBottom: '10px', fontWeight: 500 }}>活動資訊和說明</p>
                            <div dangerouslySetInnerHTML={{ 
                                __html: DOMPurify.sanitize(activityInfo.Activity.content) 
                            }} />
                        </div>
                    )}

                    {data.nodes.map(node => {
                        // [node.title, node.author, node.datetime] = node.label.split(' ');
                        [node.icon, node.title, node.infoString] = node.label.split('\n\n');
                        [node.author, node.datetime] = node.infoString.split('\n');
                        const isCurrentUser = node.author === currentUser;
                        return (
                            <div 
                                key={node.id}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    maxWidth: '80%',
                                    alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
                                    paddingBottom: '8vh'
                                }}
                            >
                                
                                {
                                    uniqueEdges
                                    .filter(edge => edge.from == node.id)
                                    .map(edge => {
                                        const replyNode = nodesMap[edge.to];
                                        // Skip if replyNode doesn't exist
                                        if (!replyNode) return null;
                                        
                                        return (
                                            <div 
                                            key={replyNode.id} 
                                            style={{
                                                backgroundColor: '#f8f9fa',
                                                padding: '8px 12px',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                alignSelf: !isCurrentUser ? 'flex-start' : 'flex-end',
                                                backgroundColor: 'var(--surface-hover)'
                                            }}>
                                                {
                                                    <span style={{ fontSize: '10px', color: '#475569', marginBottom: '4px' }}>
                                                        ↪ {replyNode.author}
                                                    </span>
                                                }
                                                <div style={{ fontWeight: 500, marginBottom: '4px' }}>{replyNode.title}</div>
                                                {getNodeType(replyNode.source) === 'questionnaire'
                                                    ? <QuestionnaireMessageBubble content={replyNode.content} />
                                                    : <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(replyNode.content) }} />
                                                }
                                            </div>
                                        );
                                    })
                                }
                                {!isCurrentUser && (
                                    <div style={{ fontSize: '13px', color: '#475569', marginLeft: '12px' }}>
                                        {node.author}
                                    </div>
                                )}
                                <div style={{
                                    backgroundColor: isCurrentUser ? 'var(--secondary-light)' : 'var(--white)',
                                    padding: '12px 16px',
                                    borderRadius: '12px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => events.click_chatroom(node.id)}
                                >
                                    <div style={{ fontWeight: 500, marginBottom: '4px' }}>{node.title}</div>
                                    {getNodeType(node.source) === 'questionnaire'
                                        ? <QuestionnaireMessageBubble content={node.content} />
                                        : <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(node.content) }} />
                                    }
                                    <div style={{ fontSize: '12px', color: '#334155', marginTop: '8px' }}>
                                        {new Date(node.createdAt).toLocaleString()}
                                    </div>
                                    {!isGuestMode && (
                                        <div style={{ fontSize: '12px', color: '#334155', marginTop: '8px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                            <IconButton 
                                                size="small" 
                                                color="primary" 
                                                aria-label="reply"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    sessionStorage.setItem('nodeId', node.id);
                                                    setNodeContent(node.source);
                                                    setShowReply(true);
                                                }}
                                            >
                                                <ReplyIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton 
                                                size="small" 
                                                color="primary" 
                                                aria-label="view"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    events.click_chatroom(node.id);
                                                }}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    <div ref={messagesEndRef} />
                </div>

                {/* Toolbar */}
                <div 
                    style={{
                        position: 'fixed',
                        bottom: '100px',
                        right: '2vw',
                        display: 'flex',
                        gap: '12px',
                        zIndex: 10000,
                    }}
                >
                    <div 
                        onClick={() => {
                            scrollToBottom();
                            if (newMessageCount > 0) {
                                addNotice(`Scrolled to ${newMessageCount} new messages`, "info");
                            }
                        }}
                        style={{
                            backgroundColor: '#7d7dff',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: '20px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            userSelect: 'none',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <span>{newMessageCount>0?`↓ ${newMessageCount} 則新訊息`:"看最新訊息"}</span>
                    </div>
                </div>

            </div>
        );
    }, (prevProps, nextProps) => {
        // 只有當 nodes 或 edges 的內容真正改變時才重新渲染
        return JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data);
    });


    const handleSendMessage = async (input_message) => {
        if (!input_message.trim()) return;

        const userName = localStorage.getItem('userName') || '匿名用戶';
        const lines = input_message.split('\n');
        const title = lines[0];
        const content = lines.slice(1).join('\n');

        const ideaData = {
            title: title,
            content: content || " ",
            tags: "idea",
            author: userName,
            groupId: sessionStorage.getItem('groupId')
        };

        try {
            // ✅ 使用共用的節點創建和夥伴回應邏輯
            const result = await createNodeWithPartnerResponse({
                ideaData,
                activityId: sessionStorage.getItem('activityId'),
                groupId: sessionStorage.getItem('groupId'),
                ws,
                nodes,
                edges,
                buildGroupContext: buildChatRoomContext,
                mode: 'chat',
                parentNodeId: null,
                limit: 15
            });

            if (!result.success) {
                throw result.error || new Error('Failed to create node');
            }
            
            // Scroll to bottom after sending
            setTimeout(() => {
                const container = containerRef.current;
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            }, 100);
        } catch (error) {
            console.error('Error creating node:', error);
            alert('發送失敗');
        }
    };

    return (
        <div className="home-container" style={showNavbar ? undefined : { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', minHeight: 0, padding: 0, background: '#e9e9ec' }}>
            
            {/* Notice Container */}
            <div style={{
                top: '80px',
                right: '20px',
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
            }}>
                {notices.map(notice => (
                    <Notice
                        key={notice.id}
                        type={notice.type}
                        message={notice.message}
                        onClose={() => removeNotice(notice.id)}
                    />
                ))}
            </div>

            {
                <div
                id="graph"
                style={showNavbar ? {
                    flex: "1 1 0%",
                    height: "100vh",
                    overflow: "auto",
                    position: "fixed",
                    top: "0px",
                    left: "0px",
                    marginTop: "64px",
                    right: "0px"
                } : {
                    position: 'absolute',
                    inset: 0,
                    overflow: 'auto',
                    background: '#e9e9ec'
                }}><Chat data={{ nodes, edges }} containerRef={containerRef} /></div>
            }

            {(() => {
                const ViewNode = resolveViewNodeComponent(getNodeType(nodeContent), 'chat');
                if (!ViewNode) return null;
                return (
                    <ViewNode
                        open={open}
                        onClose={handleClose}
                        nodeContent={nodeContent}
                        ws={ws}
                        groupId={groupId}
                        activityId={activityId}
                        onEditorOpen={() => setIsEditorOpen(true)}
                        onEditorClose={() => setIsEditorOpen(false)}
                        graphNodes={nodes}
                        graphEdges={edges}
                        buildGroupContext={buildChatRoomContext}
                        mode="chat"
                        onRefresh={getNodes}
                        onNodeUpdated={(nodeId) => {
                            if (nodeContent && nodeContent.id === nodeId) {
                                fetchNodeData(nodeId);
                            }
                        }}
                        onNodeDeleted={(nodeId) => {
                            if (nodeContent && nodeContent.id === nodeId) {
                                setNodeContent(null);
                                setOpen(false);
                            }
                        }}
                    />
                );
            })()}
            {showReply && nodeContent && (
                <ChatRoomCreateReply
                    open={showReply}
                    onClose={() => setShowReply(false)}
                    nodeContent={nodeContent}
                    ws={ws}
                    groupId={groupId}
                    graphNodes={nodes}
                    graphEdges={edges}
                    buildGroupContext={buildChatRoomContext}
                    mode="chat"
                />
            )}
            {
                activityInfo && activityInfo.settings.helper_system &&(
                    <div style={{ height: showNavbar ? '100vh' : '100%' }}>
                        <CallAgent />
                    </div>
                )
            }

            {/* Input area - 訪客模式隱藏 */}
            {!open && !isEditorOpen && !showReply && !isGuestMode && (
                <div style={{
                    position: showNavbar ? 'fixed' : 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: 'var(--white)',
                    padding: '12px 16px',
                    borderTop: '1px solid rgba(0,0,0,0.1)',
                    zIndex: 100,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    maxWidth: '100%',
                    transform: 'translateY(0)',
                    transition: 'transform 0.2s'
                }}>
                    <TextField
                        id="input-message"
                        fullWidth
                        placeholder="輸入新想法..."
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage(e.target.value);
                                document.getElementById('input-message').value = '';
                            }
                        }}
                        multiline
                        maxRows={4}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: semanticColors.lightBg,
                                '&:hover': {
                                    backgroundColor: '#f5f6f7',
                                },
                                '& fieldset': {
                                    borderColor: 'transparent',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'transparent',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#7d7dff',
                                },
                            },
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={() => {
                            handleSendMessage(document.getElementById('input-message').value);
                            document.getElementById('input-message').value = '';

                        }}
                        style={{
                            backgroundColor: '#7d7dff',
                            color: 'white',
                            padding: '10px 20px',
                            minWidth: '100px',
                            borderRadius: '8px',
                            textTransform: 'none',
                            boxShadow: 'none'
                        }}
                    >
                        發表想法
                    </Button>
                </div>
            )}

            {/* 小組工具面板 - 訪客模式隱藏 */}
            {!isGuestMode && (
                <GroupToolPanel 
                    activityId={activityId}
                    groupId={groupId}
                    ws={ws}
                    onNodeAdded={(newNode) => {
                        // 重新載入節點數據
                        getNodes();
                    }}
                    mode="chatroom"
                />
            )}
        </div>
    );
}
